import { Modal, ModalBody, ModalHeader } from "flowbite-react";
import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

interface ListenMediaModalProps {
  isOpen: boolean;
  toggle: () => void;
  audioUrl: string;
}

const ListenMediaModal: React.FC<ListenMediaModalProps> = ({
  isOpen,
  toggle,
  audioUrl,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Clean up when modal closes
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current.load();
      }
      return;
    }

    if (!audioUrl) return;

    // Wait for audio element to be ready
    const initAudio = () => {
      if (!audioRef.current) {
        setTimeout(initAudio, 50);
        return;
      }

      const audio = audioRef.current;

      if (Hls.isSupported()) {
        // Cleanup previous HLS instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }

        const hls = new Hls();
        hls.loadSource(audioUrl);
        hls.attachMedia(audio);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          audio.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("Fatal network error, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("Fatal media error, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                console.error("Fatal error, cannot recover");
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
        // Native HLS support (Safari)
        audio.src = audioUrl;
        audio.addEventListener("loadedmetadata", () => {
          audio.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      } else {
        // Fallback for other formats
        audio.src = audioUrl;
        audio.addEventListener("loadedmetadata", () => {
          audio.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      }
    };

    initAudio();
  }, [audioUrl, isOpen]);

  return (
    <Modal show={isOpen} onClose={toggle} size="lg">
      <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
        <strong>Listen to Audio</strong>
      </ModalHeader>
      <ModalBody>
        {audioUrl ? (
          <div className="flex items-center justify-center">
            <audio ref={audioRef} className="w-full" controls>
              <track kind="captions" />
              Your browser does not support the audio tag.
            </audio>
          </div>
        ) : (
          <p className="text-center">No audio available</p>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ListenMediaModal;
